/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Global console top bar: project, search (⌘K), Compute Advisor, wallet
// balance, notifications and account. On phones a compact bar (logo,
// Advisor, account, menu) sits above it and only project + search remain.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { useSidebar } from '../../contexts/SidebarContext';
import { useSelectedProject } from '../../hooks/useSelectedProject';
import { useWallet } from '../../hooks/useWallet';
import { profileService } from '../../services/api';
import { getConsoleNav } from '../../config/consoleNav';
import { isQuickNotesEnabled } from '../../config/featureFlags';
import NotificationDropdown from '../notifications/NotificationDropdown';
import CreateOrganizationModal from '../organization/CreateOrganizationModal';
import { useOrgStore } from '../../store/orgStore';
import { formatINR } from '../console/ui';
import { Logo } from './Sidebar';
import {
  CompassIc, SearchIc, ChevronDownIc, MenuIc, CloseIc, PlusIc, ExternalIc, SlidersIc, BookIc, LogoutIc, ChatIc,
} from '../console/icons';

interface ProfileData {
  profile_photo_url?: string;
  name?: string;
}

const useProfile = () =>
  useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      try {
        return (await profileService.get()).data as ProfileData;
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

const Avatar: React.FC<{ size?: number }> = ({ size = 40 }) => {
  const { user } = useAuthStore();
  const { data: profile } = useProfile();
  const name = profile?.name || user?.name || 'User';
  return profile?.profile_photo_url ? (
    <img src={profile.profile_photo_url} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />
  ) : (
    <span
      className="rounded-full bg-[#6D4AD8] text-white grid place-items-center font-semibold"
      style={{ width: size, height: size }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );
};

const AccountMenu: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { data: profile } = useProfile();
  const name = profile?.name || user?.name || 'User';
  const { organizations, selectedOrgId, fetchOrganizations, selectOrg } = useOrgStore();
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);
  const item = 'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ps-text data-[focus]:bg-ps-raised';
  const orgItem = 'w-full flex items-center justify-between gap-3 px-4 py-2 text-sm text-left data-[focus]:bg-ps-raised';
  return (
    <>
    <Menu as="div" className="relative">
      <MenuButton
        aria-label={`Account, ${name}`}
        className={`rounded-full grid place-items-center ${compact ? 'w-11 h-11' : 'w-10 h-10'} focus:outline-none focus-visible:ring-2 focus-visible:ring-ps-accent`}
      >
        <Avatar size={compact ? 36 : 40} />
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-50 mt-2 w-64 rounded-xl border border-ps-strong bg-ps-card shadow-xl shadow-black/50 py-1 focus:outline-none"
      >
        <div className="px-4 py-3 border-b border-ps-border">
          <p className="text-sm font-medium text-ps-text truncate">{name}</p>
          <p className="text-xs text-ps-subtle truncate">{user?.email}</p>
        </div>
        <div className="py-1 border-b border-ps-border">
          <p className="px-4 pt-1 pb-1 text-[11px] tracking-[0.08em] uppercase text-ps-subtle font-semibold">Account</p>
          <MenuItem>
            <button type="button" onClick={() => selectOrg(null)} className={orgItem}>
              <span className="truncate">Personal</span>
              {!selectedOrgId && <span className="text-ps-accent text-xs">Current</span>}
            </button>
          </MenuItem>
          {organizations.map((org) => (
            <MenuItem key={org.id}>
              <button type="button" onClick={() => selectOrg(org.id)} className={orgItem}>
                <span className="truncate">{org.name}</span>
                {selectedOrgId === org.id && <span className="text-ps-accent text-xs">Current</span>}
              </button>
            </MenuItem>
          ))}
          {selectedOrgId && (
            <MenuItem>
              <Link to="/organization/settings" className={orgItem}>
                Organisation settings
              </Link>
            </MenuItem>
          )}
          <MenuItem>
            <button type="button" onClick={() => setShowCreateOrg(true)} className={`${orgItem} text-ps-accent`}>
              <span className="flex items-center gap-2">
                <PlusIc /> Create organisation
              </span>
            </button>
          </MenuItem>
        </div>
        <MenuItem>
          <Link to="/settings/profile" className={item}>
            <SlidersIc /> Settings
          </Link>
        </MenuItem>
        {isQuickNotesEnabled() && (
          <MenuItem>
            <Link to="/quick-notes" className={item}>
              <ChatIc /> Quick Notes
            </Link>
          </MenuItem>
        )}
        <MenuItem>
          <a href="https://docs.podstack.ai" target="_blank" rel="noopener noreferrer" className={item}>
            <BookIc /> Documentation
          </a>
        </MenuItem>
        <div className="border-t border-ps-border mt-1 pt-1">
          <MenuItem>
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
              className={`${item} text-ps-err`}
            >
              <LogoutIc /> Log out
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </Menu>
    {showCreateOrg && <CreateOrganizationModal onClose={() => setShowCreateOrg(false)} />}
    </>
  );
};

const ProjectPicker: React.FC = () => {
  const { selectedProject, projects, setSelectedProject, autoSelectFirstProject } = useSelectedProject();
  useEffect(() => {
    autoSelectFirstProject();
  }, [autoSelectFirstProject]);
  const item = 'w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm text-left data-[focus]:bg-ps-raised';
  return (
    <Menu as="div" className="relative">
      <MenuButton className="ps-btn2 max-w-[240px]">
        <span className="text-ps-subtle">Project</span>
        <span className="truncate">{selectedProject?.name || 'Select'}</span>
        <ChevronDownIc />
      </MenuButton>
      <MenuItems
        anchor="bottom start"
        className="z-50 mt-2 w-64 max-h-[60vh] overflow-y-auto rounded-xl border border-ps-strong bg-ps-card shadow-xl shadow-black/50 py-1 focus:outline-none"
      >
        {projects.map((p: any) => (
          <MenuItem key={p.id}>
            <button type="button" onClick={() => setSelectedProject(p.id)} className={item}>
              <span className="truncate">{p.name}</span>
              {p.id === selectedProject?.id && <span className="text-ps-accent text-xs">Current</span>}
            </button>
          </MenuItem>
        ))}
        <div className="border-t border-ps-border mt-1 pt-1">
          <MenuItem>
            <Link to="/projects" className={`${item} text-ps-accent`}>
              <span className="flex items-center gap-2">
                <PlusIc /> New project
              </span>
            </Link>
          </MenuItem>
          <MenuItem>
            <Link to="/projects" className={item}>
              Manage projects
            </Link>
          </MenuItem>
        </div>
      </MenuItems>
    </Menu>
  );
};

interface Destination {
  label: string;
  group: string;
  href: string;
  external?: boolean;
}

const QUICK_ACTIONS: Destination[] = [
  { label: 'Launch a GPU pod', group: 'Actions', href: '/pods/create' },
  { label: 'Add funds', group: 'Actions', href: '/billing/payments' },
  { label: 'Create NFS volume', group: 'Actions', href: '/volumes' },
  { label: 'Ask Compute Advisor', group: 'Actions', href: '/advisor' },
  { label: 'Pod templates', group: 'QuickPods', href: '/pods/templates' },
  { label: 'Invoices', group: 'Billing', href: '/billing/invoices' },
  { label: 'Transactions', group: 'Billing', href: '/billing/transactions' },
  { label: 'SSH keys', group: 'Settings', href: '/settings/access-keys' },
  { label: 'API keys', group: 'Settings', href: '/settings/access-keys' },
  { label: 'Audit log', group: 'Projects', href: '/projects/audit-log' },
  { label: 'Documentation', group: 'Help', href: 'https://docs.podstack.ai', external: true },
];

const SearchBox: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const all = useMemo<Destination[]>(() => {
    const nav = getConsoleNav().flatMap((g) =>
      g.items.map((i) => ({ label: i.name, group: g.label || 'Console', href: i.href, external: i.external })),
    );
    return [...nav, ...QUICK_ACTIONS];
  }, []);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return all.slice(0, 8);
    return all.filter((d) => `${d.label} ${d.group}`.toLowerCase().includes(t)).slice(0, 8);
  }, [q, all]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const go = (d: Destination) => {
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
    if (d.external) window.open(d.href, '_blank', 'noopener,noreferrer');
    else navigate(d.href);
  };

  return (
    <div className="relative flex-1 basis-[260px] min-w-0 max-[820px]:basis-full">
      <label className="flex items-center gap-2 min-h-[40px] px-3 bg-ps-card border border-ps-border rounded-md text-ps-subtle focus-within:border-ps-strong">
        <SearchIc />
        <span className="sr-only">Search Podstack</span>
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-controls="console-search-results"
          aria-autocomplete="list"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setIdx(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setIdx((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setIdx((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && results[idx]) {
              e.preventDefault();
              go(results[idx]);
            } else if (e.key === 'Escape') {
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          placeholder="Search services, pods, models, docs…"
          className="flex-1 min-w-0 bg-transparent border-0 p-0 text-ps-text text-sm placeholder-ps-subtle focus:outline-none focus:ring-0"
        />
        <kbd className="max-[820px]:hidden font-mono text-xs px-1.5 py-0.5 border border-ps-strong rounded-md text-ps-muted">⌘K</kbd>
      </label>
      {open && results.length > 0 && (
        <ul
          id="console-search-results"
          role="listbox"
          className="absolute z-50 left-0 right-0 mt-2 rounded-xl border border-ps-strong bg-ps-card shadow-xl shadow-black/50 py-1 max-h-[60vh] overflow-y-auto"
        >
          {results.map((d, i) => (
            <li key={d.group + d.label} role="option" aria-selected={i === idx}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => go(d)}
                onMouseEnter={() => setIdx(i)}
                className={`w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm text-left ${i === idx ? 'bg-ps-raised' : ''}`}
              >
                <span className="text-ps-text">{d.label}</span>
                <span className="flex items-center gap-1.5 text-xs text-ps-subtle">
                  {d.group}
                  {d.external && <ExternalIc className="w-3.5 h-3.5" />}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Balance: React.FC = () => {
  const { wallet, isLoading } = useWallet();
  if (isLoading || !wallet) return null;
  const low = (wallet.balance ?? 0) < 0 || (wallet as any).is_low_balance;
  return (
    <Link to="/billing" className={`ps-btn2 ${low ? 'text-ps-err' : ''}`} title="Wallet balance">
      {formatINR(wallet.balance, (wallet as any).currency)}
    </Link>
  );
};

/** Phone-only bar: logo, Advisor, account, menu toggle. */
export const MobileBar: React.FC = () => {
  const { isOpen, toggle } = useSidebar();
  const mic =
    'w-11 h-11 flex-none grid place-items-center rounded-md border border-ps-strong bg-ps-raised text-ps-text hover:bg-ps-border';
  return (
    <div className="min-[821px]:hidden flex items-center gap-2 px-4 py-2 bg-ps-chrome border-b border-ps-raised">
      <Link to="/" aria-label="Podstack Cloud home" className="flex items-center gap-2.5 min-h-[44px]">
        <Logo />
      </Link>
      <span className="flex-1" />
      <Link to="/advisor" aria-label="Compute Advisor" className={mic}>
        <CompassIc />
      </Link>
      <AccountMenu compact />
      <button
        type="button"
        className={mic}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
        aria-controls="console-nav"
        onClick={toggle}
      >
        {isOpen ? <CloseIc /> : <MenuIc />}
      </button>
    </div>
  );
};

const TopBar: React.FC = () => (
  <header className="sticky top-0 z-30 flex flex-wrap items-center gap-2.5 px-4 py-2.5 min-[821px]:px-7 min-[821px]:py-3 border-b border-ps-raised bg-ps-chrome">
    <ProjectPicker />
    <SearchBox />
    <div className="max-[820px]:hidden flex items-center gap-2.5">
      <Link to="/advisor" className="ps-btn2">
        <CompassIc />
        <span className="max-[1100px]:hidden">Compute Advisor</span>
      </Link>
      <Balance />
      <NotificationDropdown />
      <AccountMenu />
    </div>
  </header>
);

export default TopBar;
